import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { verifyEmail } from '../features/auth/authApi';

type Status = 'loading' | 'success' | 'error';

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }
    verifyEmail(token)
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'));
  }, [token]);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-paper-100 px-4 font-manuscript">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #8c2d1e 1.5px, transparent 0)',
          backgroundSize: '28px 28px',
        }}
      />

      <div className="relative w-full max-w-sm">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full">
            <img src="/logo.png" alt={t('common.appName')} className="h-full w-full object-cover" />
          </span>
          <span className="font-display text-lg font-semibold text-manuscript-ink">{t('common.appName')}</span>
        </Link>

        <div className="rounded-sm border border-manuscript-line-strong bg-paper-50 p-8 text-center">
          {status === 'loading' && (
            <p className="text-sm text-manuscript-ink-soft">{t('common.loading')}</p>
          )}
          {status === 'success' && (
            <>
              <h1 className="font-display text-xl font-semibold text-manuscript-ink">
                {t('auth.verifyEmail.successTitle')}
              </h1>
              <p className="mt-3 text-sm text-manuscript-ink-soft">{t('auth.verifyEmail.successBody')}</p>
              <Link
                to="/login"
                className="mt-6 inline-block rounded-sm bg-seal-500 px-6 py-3 text-sm font-semibold text-paper-50 transition-colors hover:bg-seal-400"
              >
                {t('auth.verifyEmail.loginCta')}
              </Link>
            </>
          )}
          {status === 'error' && (
            <>
              <h1 className="font-display text-xl font-semibold text-manuscript-ink">
                {t('auth.verifyEmail.errorTitle')}
              </h1>
              <p className="mt-3 text-sm text-manuscript-ink-soft">{t('auth.verifyEmail.errorBody')}</p>
              <Link to="/login" className="mt-6 inline-block text-sm font-medium text-seal-500 hover:text-seal-400">
                {t('auth.checkEmail.backToLogin')}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
